import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { VERTICALS, verticalCopy } from '@/lib/verticals';
import { useT, useLang, useRaw } from '@/lib/i18n';
import { useTheme } from '@/lib/theme';
import { useAuth } from '@/lib/auth';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { ChevronDown, Menu, X, Sun, Moon, User, LogOut, LayoutTemplate } from 'lucide-react';

export const Navbar = () => {
  const t = useT();
  const raw = useRaw();
  const { lang, setLang } = useLang();
  const { theme, toggle } = useTheme();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const isDark = theme === 'dark';
  const markSrc = isDark ? '/brand/logo-mark-white.png' : '/brand/logo-mark.png';

  const NAV_LINKS = [
    { label: t('navbar.home'), path: '/', testid: 'navbar-link-home' },
    { label: t('navbar.applications'), path: '/applications', testid: 'navbar-link-applications' },
    { label: t('navbar.generator'), path: '/generateur', testid: 'navbar-link-generateur' },
    { label: t('navbar.pricing'), path: '/tarifs', testid: 'navbar-link-tarifs' },
    { label: t('navbar.contact'), path: '/contact', testid: 'navbar-link-contact' },
  ];

  return (
    <header
      data-testid="site-navbar"
      style={{ top: 'var(--om-banner-h, 0px)' }}
      className={`fixed inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white/75 backdrop-blur-xl shadow-[0_1px_0_rgba(10,14,26,0.06)]' : 'bg-transparent'
      }`}
    >
      <div className="om-container flex h-[72px] items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5" data-testid="navbar-logo">
          <img src={markSrc} alt="OpenMeta" className="h-9 w-9" width="36" height="36" />
          <span className="text-[19px] font-extrabold tracking-tight text-[var(--om-ink)]">
            OpenMeta
          </span>
        </Link>

        <nav className="hidden items-center gap-1 lg:flex">
          <NavLink
            to="/"
            end
            className={({ isActive }) =>
              `rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
                isActive ? 'text-[var(--om-ink)]' : 'text-[var(--om-muted)] hover:text-[var(--om-ink)]'
              }`
            }
            data-testid="navbar-link-home"
          >
            {t('navbar.home')}
          </NavLink>

          <DropdownMenu>
            <DropdownMenuTrigger
              className="inline-flex items-center gap-1 rounded-full px-4 py-2 text-sm font-semibold text-[var(--om-muted)] outline-none transition-colors hover:text-[var(--om-ink)] focus:text-[var(--om-ink)]"
              data-testid="navbar-univers-trigger"
            >
              {t('navbar.universe')} <ChevronDown size={14} strokeWidth={2.4} />
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="start"
              className="w-72 rounded-2xl border-[var(--om-border)] bg-white/95 p-2 shadow-[0_20px_60px_-20px_rgba(10,14,26,0.25)] backdrop-blur-xl"
            >
              {VERTICALS.map((v) => {
                const vc = verticalCopy(raw, v.code);
                return (
                  <DropdownMenuItem key={v.code} asChild>
                    <Link
                      to={v.path}
                      className="flex cursor-pointer items-center gap-3 rounded-xl px-3 py-2.5 focus:bg-[var(--om-bg)]"
                      data-testid={`navbar-univers-${v.code}`}
                    >
                      <span
                        className="h-2.5 w-2.5 rounded-full"
                        style={{ background: v.color, boxShadow: `0 0 0 4px ${v.tint}` }}
                      />
                      <span className="flex flex-col">
                        <span className="text-sm font-semibold text-[var(--om-ink)]">{vc.label}</span>
                        <span className="text-xs text-[var(--om-muted)]">{vc.short}</span>
                      </span>
                    </Link>
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>

          {NAV_LINKS.slice(1).map((l) => (
            <NavLink
              key={l.path}
              to={l.path}
              className={({ isActive }) =>
                `rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
                  isActive ? 'text-[var(--om-ink)]' : 'text-[var(--om-muted)] hover:text-[var(--om-ink)]'
                }`
              }
              data-testid={l.testid}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden items-center gap-2 lg:flex">
          <LangSwitcher lang={lang} setLang={setLang} t={t} />
          <ThemeToggle theme={theme} toggle={toggle} t={t} />
          {user ? (
            <AccountMenu user={user} onLogout={() => { logout(); navigate('/'); }} t={t} />
          ) : (
            <>
              <Link to="/connexion" className="om-btn-ghost h-11 text-xs" data-testid="navbar-login-link">
                {t('auth.login_cta')}
              </Link>
              <Link to="/inscription" className="om-btn-primary" data-testid="navbar-register-link">
                {t('auth.register_cta')}
              </Link>
            </>
          )}
        </div>

        <button
          onClick={() => setMobileOpen((v) => !v)}
          className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-[var(--om-border)] bg-white lg:hidden"
          aria-label={t('common.open_menu')}
          data-testid="navbar-mobile-toggle"
        >
          {mobileOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      {mobileOpen && (
        <div className="lg:hidden">
          <div className="om-container pb-6">
            <div className="om-glass rounded-3xl p-4">
              <nav className="flex flex-col gap-1" data-testid="navbar-mobile-menu">
                <Link to="/" className="rounded-xl px-4 py-3 text-sm font-semibold text-[var(--om-ink)] hover:bg-[var(--om-bg)]">
                  {t('navbar.home')}
                </Link>
                <div className="mt-2 px-4 text-xs font-semibold uppercase tracking-wider text-[var(--om-muted-2)]">
                  {t('navbar.universe')}
                </div>
                {VERTICALS.map((v) => {
                  const vc = verticalCopy(raw, v.code);
                  return (
                    <Link
                      key={v.code}
                      to={v.path}
                      className="flex items-center gap-3 rounded-xl px-4 py-2.5 text-sm text-[var(--om-ink)] hover:bg-[var(--om-bg)]"
                      data-testid={`navbar-mobile-univers-${v.code}`}
                    >
                      <span className="h-2 w-2 rounded-full" style={{ background: v.color }} />
                      {vc.label}
                    </Link>
                  );
                })}
                <div className="my-2 h-px bg-[var(--om-border)]" />
                {NAV_LINKS.slice(1).map((l) => (
                  <Link key={l.path} to={l.path} className="rounded-xl px-4 py-3 text-sm font-semibold text-[var(--om-ink)] hover:bg-[var(--om-bg)]">
                    {l.label}
                  </Link>
                ))}
                <div className="mt-3 flex items-center justify-between gap-3 px-2">
                  <LangSwitcher lang={lang} setLang={setLang} t={t} testidPrefix="navbar-mobile" />
                  <ThemeToggle theme={theme} toggle={toggle} t={t} testid="navbar-mobile-theme-toggle" />
                </div>
                <a href="#waitlist" className="om-btn-primary mt-3 w-full">
                  {t('navbar.cta_waitlist')}
                </a>
              </nav>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

function LangSwitcher({ lang, setLang, t, testidPrefix = 'navbar' }) {
  return (
    <div
      role="group"
      aria-label={t('common.switch_lang')}
      className="inline-flex overflow-hidden rounded-full border border-[var(--om-border)] bg-white/70 backdrop-blur"
      data-testid={`${testidPrefix}-lang-switcher`}
    >
      {['fr', 'en'].map((code) => {
        const active = lang === code;
        return (
          <button
            key={code}
            type="button"
            onClick={() => setLang(code)}
            className={`h-8 px-3 text-[11px] font-bold uppercase tracking-wider transition-colors ${
              active
                ? 'bg-[var(--om-ink)] text-[var(--om-btn-primary-fg)]'
                : 'text-[var(--om-muted)] hover:text-[var(--om-ink)]'
            }`}
            data-testid={`${testidPrefix}-lang-${code}`}
            aria-pressed={active}
          >
            {code.toUpperCase()}
          </button>
        );
      })}
    </div>
  );
}

function ThemeToggle({ theme, toggle, t, testid = 'navbar-theme-toggle' }) {
  const isDark = theme === 'dark';
  return (
    <button
      type="button"
      onClick={toggle}
      aria-label={isDark ? t('common.switch_theme_light') : t('common.switch_theme_dark')}
      title={isDark ? t('common.switch_theme_light') : t('common.switch_theme_dark')}
      className="grid h-9 w-9 place-items-center rounded-full border border-[var(--om-border)] bg-white/70 text-[var(--om-ink)] backdrop-blur transition-all hover:-translate-y-[1px] hover:shadow-[0_10px_20px_-14px_rgba(10,14,26,0.25)]"
      data-testid={testid}
    >
      {isDark ? <Sun size={16} strokeWidth={2.4} /> : <Moon size={16} strokeWidth={2.4} />}
    </button>
  );
}


function AccountMenu({ user, onLogout, t }) {
  const emailShort = user?.email
    ? user.email.length > 22
      ? `${user.email.slice(0, 10)}…${user.email.slice(-10)}`
      : user.email
    : '';
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className="inline-flex h-9 items-center gap-2 rounded-full border border-[var(--om-border)] bg-white/70 px-3 py-1.5 text-[12px] font-semibold text-[var(--om-ink)] backdrop-blur transition-all hover:-translate-y-[1px] hover:shadow-[0_10px_20px_-14px_rgba(10,14,26,0.25)]"
        data-testid="navbar-account-menu"
        aria-label={t('auth.account_menu_title')}
      >
        <span className="grid h-6 w-6 place-items-center rounded-full bg-[var(--om-ink)] text-white">
          <User size={12} strokeWidth={2.4} />
        </span>
        <span className="hidden xl:inline max-w-[160px] truncate" data-testid="navbar-account-email">{emailShort}</span>
        <ChevronDown size={12} strokeWidth={2.4} className="opacity-70" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <div className="px-3 pt-2 pb-2 text-[10px] font-semibold uppercase tracking-wider text-[var(--om-muted-2)]">
          {t('auth.account_menu_title')}
        </div>
        <div className="px-3 pb-2 text-[11px] text-[var(--om-muted)] break-all" data-testid="navbar-account-full-email">
          {user?.email}
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild data-testid="navbar-account-studio">
          <Link to="/studio" className="flex items-center gap-2 cursor-pointer">
            <LayoutTemplate size={13} strokeWidth={2.4} />
            {t('auth.my_apps')}
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onSelect={(e) => { e.preventDefault(); onLogout(); }}
          data-testid="navbar-account-logout"
          className="flex items-center gap-2 cursor-pointer text-rose-600 focus:text-rose-600"
        >
          <LogOut size={13} strokeWidth={2.4} />
          {t('auth.logout')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
